import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  UserX,
  UserCheck,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Shield,
  Users,
  AlertCircle,
  X,
} from 'lucide-react';
import { getAdminUsers, toggleUserSuspension, deleteUser } from '../../services/adminService';
import type { AdminUser, AdminUsersFilter } from '../../types/admin';
import { logger } from '../../lib/logger';

// Confirmation Modal for delete
function DeleteConfirmModal({
  user,
  onConfirm,
  onCancel,
}: {
  user: AdminUser;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (confirmEmail !== user.email) return;
    setIsDeleting(true);
    await onConfirm();
    setIsDeleting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">Nutzer endgültig löschen</h3>
        </div>

        <p className="text-slate-400 mb-4">
          Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten des Nutzers werden
          unwiderruflich gelöscht, einschließlich:
        </p>

        <ul className="text-sm text-slate-500 mb-4 space-y-1 list-disc list-inside">
          <li>Profil und Einstellungen</li>
          <li>Alle Leads und Aktivitäten</li>
          <li>Alle Formulare und Einsendungen</li>
          <li>Alle Website-Seiten und Blöcke</li>
          <li>Alle Medien-Uploads</li>
        </ul>

        <div className="bg-slate-800 rounded-xl p-4 mb-4">
          <p className="text-sm text-slate-400 mb-2">
            Zur Bestätigung gib die E-Mail-Adresse des Nutzers ein:
          </p>
          <p className="text-sm font-mono text-orange-400 mb-3">{user.email}</p>
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="E-Mail eingeben"
            className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={handleDelete}
            disabled={confirmEmail !== user.email || isDeleting}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Lösche...' : 'Endgültig löschen'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<'active' | 'suspended' | ''>(
    (searchParams.get('status') as 'active' | 'suspended') || ''
  );
  const [roleFilter, setRoleFilter] = useState<'user' | 'admin' | ''>(
    (searchParams.get('role') as 'user' | 'admin') || ''
  );
  const [showFilters, setShowFilters] = useState(false);
  const [deleteModal, setDeleteModal] = useState<AdminUser | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const pageSize = 20;

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const filters: AdminUsersFilter = {};
      if (search) filters.search = search;
      if (statusFilter) filters.status = statusFilter;
      if (roleFilter) filters.role = roleFilter;

      const result = await getAdminUsers(filters, pageSize, (page - 1) * pageSize);
      setUsers(result.users);
      setTotal(result.total);
    } catch (error) {
      logger.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, search, statusFilter, roleFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (statusFilter) params.set('status', statusFilter);
    if (roleFilter) params.set('role', roleFilter);
    setSearchParams(params);
  };

  const handleToggleSuspension = async (user: AdminUser) => {
    setActionLoading(user.id);
    try {
      await toggleUserSuspension(user.id, !user.is_suspended);
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, is_suspended: !u.is_suspended } : u))
      );
    } catch (error) {
      logger.error('Error toggling suspension:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    try {
      await deleteUser(deleteModal.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteModal.id));
      setTotal((prev) => prev - 1);
      setDeleteModal(null);
    } catch (error) {
      logger.error('Error deleting user:', error);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setPage(1);
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = search || statusFilter || roleFilter;
  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Nutzerverwaltung</h1>
          <p className="text-slate-400 mt-1">{total} Nutzer insgesamt</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name oder E-Mail suchen..."
              className="w-full pl-12 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-colors ${
              hasActiveFilters
                ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filter
            {hasActiveFilters && (
              <span className="w-2 h-2 bg-orange-500 rounded-full" />
            )}
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-colors font-medium"
          >
            Suchen
          </button>
        </form>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'active' | 'suspended' | '')}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                <option value="active">Aktiv</option>
                <option value="suspended">Gesperrt</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Rolle</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as 'user' | 'admin' | '')}
                className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Alle</option>
                <option value="user">Nutzer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {hasActiveFilters && (
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                  Filter zurücksetzen
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Keine Nutzer gefunden</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Nutzer
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Registriert
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Letzte Aktivität
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Setup
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Leads
                    </th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Seiten
                    </th>
                    <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-6 py-4">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {user.profile_image_url ? (
                            <img
                              src={user.profile_image_url}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-slate-400" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white">
                                {user.full_name || 'Kein Name'}
                              </p>
                              {user.role === 'admin' && (
                                <Shield className="w-4 h-4 text-orange-400" />
                              )}
                            </div>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300">
                        {formatDateTime(user.last_activity)}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            user.is_suspended
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-emerald-500/20 text-emerald-400'
                          }`}
                        >
                          {user.is_suspended ? 'Gesperrt' : 'Aktiv'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-orange-500 rounded-full"
                              style={{ width: `${user.setup_progress}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-400">{user.setup_progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-300">{user.lead_count}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              user.business_published
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            B
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              user.products_published
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-slate-700 text-slate-400'
                            }`}
                          >
                            P
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Link
                            to={`/admin/users/${user.id}`}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            title="Details anzeigen"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                          <button
                            onClick={() => handleToggleSuspension(user)}
                            disabled={actionLoading === user.id}
                            className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                              user.is_suspended
                                ? 'text-emerald-400 hover:bg-emerald-500/20'
                                : 'text-amber-400 hover:bg-amber-500/20'
                            }`}
                            title={user.is_suspended ? 'Entsperren' : 'Sperren'}
                          >
                            {user.is_suspended ? (
                              <UserCheck className="w-4 h-4" />
                            ) : (
                              <UserX className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setDeleteModal(user)}
                            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-slate-800">
              {users.map((user) => (
                <div key={user.id} className="p-4 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {user.profile_image_url ? (
                        <img
                          src={user.profile_image_url}
                          alt=""
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center">
                          <Users className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{user.full_name || 'Kein Name'}</p>
                          {user.role === 'admin' && <Shield className="w-4 h-4 text-orange-400" />}
                        </div>
                        <p className="text-sm text-slate-400">{user.email}</p>
                      </div>
                    </div>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.is_suspended
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-emerald-500/20 text-emerald-400'
                      }`}
                    >
                      {user.is_suspended ? 'Gesperrt' : 'Aktiv'}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-lg font-semibold text-white">{user.setup_progress}%</p>
                      <p className="text-xs text-slate-400">Setup</p>
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-white">{user.lead_count}</p>
                      <p className="text-xs text-slate-400">Leads</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-300">{formatDate(user.created_at)}</p>
                      <p className="text-xs text-slate-400">Registriert</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Link
                      to={`/admin/users/${user.id}`}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Details
                    </Link>
                    <button
                      onClick={() => handleToggleSuspension(user)}
                      disabled={actionLoading === user.id}
                      className={`px-4 py-2 rounded-xl transition-colors disabled:opacity-50 ${
                        user.is_suspended
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}
                    >
                      {user.is_suspended ? (
                        <UserCheck className="w-4 h-4" />
                      ) : (
                        <UserX className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setDeleteModal(user)}
                      className="px-4 py-2 bg-red-500/20 text-red-400 rounded-xl transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
                <p className="text-sm text-slate-400">
                  Seite {page} von {totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="p-2 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="p-2 bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <DeleteConfirmModal
          user={deleteModal}
          onConfirm={handleDelete}
          onCancel={() => setDeleteModal(null)}
        />
      )}
    </div>
  );
}
